import { Bone, Group, Mesh, MeshPhysicalMaterial, MeshStandardMaterial, Object3D, SkinnedMesh } from 'three';
import type { Material } from 'three';
import { fleshMaterial } from './flesh';
import { buildGlove, gloveLength } from './glove';
import type { GloveColours } from './glove';
import { blob } from './shapes';
import { SkinFollower } from './skin';

export interface BoxerLook {
  readonly skin: number;
  /** His beard and brows. */
  readonly hair: number;
  readonly gloves: GloveColours;
  readonly trunks: number;
  readonly waistband: number;
  readonly boots: number;
}

/** One arm's joints: the shoulder turns the upper arm, the elbow turns the forearm, the glove rides on the wrist. */
export interface ArmRig {
  readonly shoulder: Object3D;
  readonly upper: Object3D;
  readonly fore: Object3D;
  readonly glove: Object3D;
}

/** The joints the animation moves. The boxer faces +Z; his left side is +X. */
export interface BoxerRig {
  readonly root: Group;
  readonly hips: Object3D;
  readonly chest: Object3D;
  readonly neck: Object3D;
  readonly head: Object3D;
  /** The middle of his skull, where the player's punches to the head are aimed from. */
  readonly skull: Object3D;
  readonly left: ArmRig;
  readonly right: ArmRig;
  readonly legs: readonly { readonly thigh: Object3D; readonly shin: Object3D }[];
  readonly face: FaceRig;
  /** Moves the skinned body after the joints: call once the animation has placed them. */
  readonly body: SkinFollower;
  readonly upperArmLength: number;
  readonly foreArmLength: number;
  /** From his wrist to the tip of his knuckles. */
  readonly gloveLength: number;
}

/** His joints alone, in their rest places, with nothing on them to see. */
export type BoxerJoints = Omit<BoxerRig, 'skull' | 'face' | 'body'>;

/** The moving parts of his face; each sits at its rest place, so the animation moves it by offsets from zero. */
export interface FaceRig {
  readonly eyes: readonly Object3D[];
  readonly lids: readonly Object3D[];
  readonly brows: readonly Object3D[];
  readonly mouth: Object3D;
}

const upperArmLength = 0.3;
const foreArmLength = 0.28;
const gloveScale = 1.12;

/**
 * An opponent: his body, head and beard a model made in Blender (art/boxer.py), moved by an invisible rig of joints
 * that the animation drives; his eyes, brows and mouth, and his gloves, made here, at the places the model marks.
 */
export function buildBoxer(look: BoxerLook, model: Object3D): BoxerRig {
  const joints = boxerJoints();
  const { root, hips, chest, neck, head, left, right, legs } = joints;
  for (const [arm, side] of [[left, 1], [right, -1]] as const) {
    const glove = buildGlove(look.gloves, side);
    // The glove's knuckles point along -Z; the forearm runs along +Z into it.
    glove.rotation.y = Math.PI;
    glove.scale.setScalar(gloveScale);
    arm.glove.add(glove);
  }

  dress(model, look);
  root.add(model);
  const mark = (name: string) => {
    const found = model.getObjectByName(name);
    if (!found) throw new Error(`The boxer model has no "${name}"`);
    return found;
  };
  const face = buildFace(mark, look);
  root.traverse((part) => {
    if (part instanceof Mesh) {
      part.castShadow = true;
      part.receiveShadow = true;
    }
  });

  // Both stand at rest now: each bone learns where it sits against its joint.
  root.updateMatrixWorld(true);
  const bone = (name: string) => {
    const found = mark(name);
    if (!(found instanceof Bone)) throw new Error(`The boxer model's "${name}" is not a bone`);
    return found;
  };
  const pairs = [
    { bone: bone('hips'), driver: hips },
    { bone: bone('chest'), driver: chest },
    { bone: bone('neck'), driver: neck },
    { bone: bone('head'), driver: head },
  ];
  for (const [tag, a] of [['L', left], ['R', right]] as const) {
    pairs.push({ bone: bone(`shoulder_${tag}`), driver: a.shoulder }, { bone: bone(`upperArm_${tag}`), driver: a.upper }, { bone: bone(`foreArm_${tag}`), driver: a.fore });
  }
  for (const [tag, leg] of [['L', legs[0]], ['R', legs[1]]] as const) {
    pairs.push({ bone: bone(`thigh_${tag}`), driver: leg.thigh }, { bone: bone(`shin_${tag}`), driver: leg.shin }, { bone: bone(`foot_${tag}`), driver: leg.shin });
  }
  const body = new SkinFollower(pairs);
  return { ...joints, skull: mark('skull'), face, body };
}

/** The rig's joints, where they stand at rest. */
export function boxerJoints(): BoxerJoints {
  const root = new Group();
  root.name = 'Boxer';
  const hips = joint(root, 0, 0.92, 0);
  const chest = joint(hips, 0, 0.08, 0);
  const neck = joint(chest, 0, 0.52, 0.02);
  const head = joint(neck, 0, 0.1, 0.02);
  const left = arm(chest, 1);
  const right = arm(chest, -1);
  const legs = [1, -1].map((side) => {
    const thigh = joint(hips, side * 0.12, -0.12, 0);
    thigh.rotation.x = Math.PI / 2;
    const shin = joint(thigh, 0, 0, 0.38);
    return { thigh, shin };
  });
  return { root, hips, chest, neck, head, left, right, legs, upperArmLength, foreArmLength, gloveLength: gloveLength * gloveScale };
}

// The model's materials by the names they have in Blender, made again with the look's colours: one model, dressed
// each time in its own colours.
function dress(model: Object3D, look: BoxerLook): void {
  const made: Record<string, Material> = {
    Skin: fleshMaterial(look.skin, true),
    Hair: new MeshStandardMaterial({ color: look.hair, roughness: 0.9 }),
    Trunks: new MeshPhysicalMaterial({ color: look.trunks, roughness: 0.5, sheen: 0.6, sheenRoughness: 0.4, sheenColor: 0xbfffd0 }),
    Waistband: new MeshStandardMaterial({ color: look.waistband, roughness: 0.55 }),
    Boots: new MeshStandardMaterial({ color: look.boots, roughness: 0.45 }),
  };
  model.traverse((part) => {
    if (!(part instanceof Mesh)) return;
    const name = (part.material as Material).name;
    const material = made[name];
    if (!material) throw new Error(`The boxer model has a material the game does not dress: "${name}"`);
    part.material = material;
    // A skinned mesh's bounds are those of the pose it was made in; animated, it would be culled at the wrong moments.
    if (part instanceof SkinnedMesh) part.frustumCulled = false;
  });
}

function arm(chest: Object3D, side: 1 | -1): ArmRig {
  const shoulder = joint(chest, side * 0.31, 0.43, -0.01);
  const upper = joint(shoulder, 0, 0, 0);
  const fore = joint(upper, 0, 0, upperArmLength);
  const glove = joint(fore, 0, 0, foreArmLength);
  return { shoulder, upper, fore, glove };
}

function buildFace(mark: (name: string) => Object3D, look: BoxerLook): FaceRig {
  const white = new MeshPhysicalMaterial({ color: 0xf4f1ea, roughness: 0.2, clearcoat: 0.6 });
  const dark = new MeshStandardMaterial({ color: 0x1a120e, roughness: 0.35 });
  const lid = fleshMaterial(look.skin);
  const hair = new MeshStandardMaterial({ color: look.hair, roughness: 0.9 });
  const eyes: Object3D[] = [];
  const lids: Object3D[] = [];
  const brows: Object3D[] = [];
  for (const tag of ['L', 'R']) {
    const socket = mark(`eye_${tag}`);
    add(socket, new Mesh(blob(0.027, 0.025, 0.022), white));
    const eye = joint(socket, 0, 0, 0);
    add(eye, new Mesh(blob(0.013, 0.015, 0.008), dark), 0, 0, 0.019);
    eyes.push(eye);
    const lidMesh = add(socket, new Mesh(blob(0.03, 0.028, 0.026), lid), 0, 0.012, 0.002);
    lidMesh.scale.y = 0.001;
    lids.push(lidMesh);
    const brow = joint(mark(`brow_${tag}`), 0, 0, 0);
    add(brow, new Mesh(blob(0.048, 0.016, 0.02), hair));
    brows.push(brow);
  }
  const mouth = joint(mark('mouth'), 0, 0, 0);
  add(mouth, new Mesh(blob(0.036, 0.013, 0.012), dark));
  return { eyes, lids, brows, mouth };
}

function joint(parent: Object3D, x: number, y: number, z: number): Object3D {
  const node = new Object3D();
  node.position.set(x, y, z);
  parent.add(node);
  return node;
}

function add<T extends Object3D>(parent: Object3D, child: T, x = 0, y = 0, z = 0): T {
  child.position.set(x, y, z);
  parent.add(child);
  return child;
}
